// src/components/admin/settings/PharmacieSettings.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import DataTableSettings from '../../common/DataTableSettings';
import ModalComponent from '../../common/ModalComponent';
import { AlertCircle, MapPin, Upload, FileText, CheckCircle, XCircle, Loader2, Pill } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import Papa from 'papaparse';

// GraphQL Queries
const GET_PHARMACIES_PAGINATED = gql`
  query GetPharmaciesPaginated($paginationInput: PaginationInput) {
    pharmaciesPaginated(paginationInput: $paginationInput) {
      pharmacies {
        id
        namePharmacy
        telPharmacy
        commissionBalance
        regionId
        villeId
        userId
        region {
          id
          nomRegion
        }
        ville {
          id
          nomVilleVillage
        }
        owner {
          id
          email
          firstName
          lastName
        }
        createdAt
        updatedAt
      }
      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

// Keep lightweight queries for dropdowns and import
const GET_REGIONS = gql`
  query GetRegions {
    regions {
      id
      nomRegion
    }
  }
`;

const GET_VILLES = gql`
  query GetVilles {
    villes {
      id
      nomVilleVillage
      regionId
      region {
        id
        nomRegion
      }
    }
  }
`;

const GET_VILLES_BY_REGION = gql`
  query GetVillesByRegion($regionId: ID!) {
    villesByRegion(regionId: $regionId) {
      id
      nomVilleVillage
      regionId
    }
  }
`;

const GET_USERS_BY_TYPE = gql`
  query GetUsersByType($userType: String!) {
    usersByType(userType: $userType) {
      id
      email
      firstName
      lastName
      userType
      isActive
    }
  }
`;

// GraphQL Mutations
const CREATE_PHARMACY = gql`
  mutation CreatePharmacy($createPharmacyInput: CreatePharmacyInput!) {
    createPharmacy(createPharmacyInput: $createPharmacyInput) {
      id
      namePharmacy
      telPharmacy
      commissionBalance
      regionId
      villeId
      userId
      region {
        id
        nomRegion
      }
      ville {
        id
        nomVilleVillage
      }
      owner {
        id
        email
        firstName
        lastName
      }
      createdAt
      updatedAt
    }
  }
`;

const CREATE_REGION = gql`
  mutation CreateRegion($createRegionInput: CreateRegionInput!) {
    createRegion(createRegionInput: $createRegionInput) {
      id
      nomRegion
      createdAt
      updatedAt
    }
  }
`;

const CREATE_VILLE = gql`
  mutation CreateVille($createVilleInput: CreateVilleInput!) {
    createVille(createVilleInput: $createVilleInput) {
      id
      nomVilleVillage
      regionId
      region {
        id
        nomRegion
      }
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_PHARMACY = gql`
  mutation UpdatePharmacy($updatePharmacyInput: UpdatePharmacyInput!) {
    updatePharmacy(updatePharmacyInput: $updatePharmacyInput) {
      id
      namePharmacy
      telPharmacy
      commissionBalance
      regionId
      villeId
      userId
      region {
        id
        nomRegion
      }
      ville {
        id
        nomVilleVillage
      }
      owner {
        id
        email
        firstName
        lastName
      }
      createdAt
      updatedAt
    }
  }
`;

const DELETE_PHARMACY = gql`
  mutation DeletePharmacy($id: ID!) {
    removePharmacy(id: $id)
  }
`;

const PharmacieSettings = () => {
  // ✅ PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Form and modal states remain the same...
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPharmacy, setCurrentPharmacy] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [filteredVilles, setFilteredVilles] = useState([]);
  const [formData, setFormData] = useState({
    namePharmacy: '',
    telPharmacy: '',
    regionId: '',
    villeId: '',
    userId: '',
    piecesJointes: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // CSV Import States remain the same...
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importStep, setImportStep] = useState(1);

  // ✅ OPTIMIZED: Paginated pharmacy query
  const { 
    data: pharmaciesData, 
    loading: pharmaciesLoading, 
    error: pharmaciesError,
    refetch: refetchPharmacies 
  } = useQuery(GET_PHARMACIES_PAGINATED, {
    variables: {
      paginationInput: {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        sortBy,
        sortOrder
      }
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  // Lightweight queries for dropdowns
  const { 
    data: regionsData, 
    loading: regionsLoading,
    refetch: refetchRegions
  } = useQuery(GET_REGIONS);

  const { 
    data: villesAllData,
    refetch: refetchVillesAll
  } = useQuery(GET_VILLES, {
    fetchPolicy: 'cache-and-network'
  });

  const { 
    data: villesData,
    refetch: refetchVilles 
  } = useQuery(GET_VILLES_BY_REGION, {
    variables: { regionId: formData.regionId || 'dummy' },
    skip: !formData.regionId,
    fetchPolicy: 'cache-and-network'
  });

  const { 
    data: usersData, 
    loading: usersLoading 
  } = useQuery(GET_USERS_BY_TYPE, {
    variables: { userType: 'PHARMACIST' }
  });

  // ✅ DEBOUNCED SEARCH: Update search with delay to avoid too many requests
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // Mutations with optimized refetch
  const [createPharmacy, { loading: createLoading }] = useMutation(CREATE_PHARMACY, {
    onCompleted: (data) => {
      if (data.createPharmacy) {
        toast.success('Pharmacie créée avec succès');
        setIsModalOpen(false);
        setFormData({
          namePharmacy: '',
          telPharmacy: '',
          regionId: '',
          villeId: '',
          userId: '',
          piecesJointes: ''
        });
        setFormErrors({});
        // ✅ OPTIMIZED: Only refetch current page
        refetchPharmacies();
      }
    },
    onError: (error) => {
      console.error('Create pharmacy error:', error);
      toast.error('Erreur lors de la création de la pharmacie');
    }
  });

  const [createRegion] = useMutation(CREATE_REGION, {
    refetchQueries: [{ query: GET_REGIONS }],
    onError: (error) => {
      console.error('Create region error:', error);
    }
  });

  const [createVille] = useMutation(CREATE_VILLE, {
    refetchQueries: [{ query: GET_VILLES }],
    onError: (error) => {
      console.error('Create ville error:', error);
    }
  });

  const [updatePharmacy, { loading: updateLoading }] = useMutation(UPDATE_PHARMACY, {
    onCompleted: (data) => {
      if (data.updatePharmacy) {
        toast.success('Pharmacie mise à jour avec succès');
        setIsModalOpen(false);
        setCurrentPharmacy(null);
        setFormData({
          namePharmacy: '',
          telPharmacy: '',
          regionId: '',
          villeId: '',
          userId: '',
          piecesJointes: ''
        });
        setFormErrors({});
        // ✅ OPTIMIZED: Only refetch current page
        refetchPharmacies();
      }
    },
    onError: (error) => {
      console.error('Update pharmacy error:', error);
      toast.error('Erreur lors de la mise à jour de la pharmacie');
    }
  });

  const [deletePharmacy, { loading: deleteLoading }] = useMutation(DELETE_PHARMACY, {
    onCompleted: () => {
      toast.success('Pharmacie supprimée avec succès');
      setIsConfirmDeleteOpen(false);
      setCurrentPharmacy(null);
      // ✅ OPTIMIZED: Check if we need to go back a page
      const totalItems = pharmaciesData?.pharmaciesPaginated?.pagination?.totalItems || 0;
      const totalPages = Math.ceil((totalItems - 1) / itemsPerPage);
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
      } else {
        refetchPharmacies();
      }
    },
    onError: (error) => {
      console.error('Delete pharmacy error:', error);
      toast.error('Erreur lors de la suppression de la pharmacie');
    }
  });

  // ✅ OPTIMIZED: Extract data from paginated response
  const pharmacies = pharmaciesData?.pharmaciesPaginated?.pharmacies || [];
  const pagination = pharmaciesData?.pharmaciesPaginated?.pagination;
  const regions = regionsData?.regions || [];
  const villesAll = villesAllData?.villes || [];
  const villes = villesData?.villesByRegion || [];
  const users = usersData?.usersByType || [];

  // ✅ PAGINATION HANDLERS
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleSearch = (search) => {
    setSearchTerm(search);
    setCurrentPage(1); // Reset to first page when searching
  };

  // CSV Import Functions
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Seuls les fichiers CSV sont acceptés');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 5MB');
      return;
    }

    setCsvFile(file);
    
    // Parse CSV
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        if (results.errors && results.errors.length > 0) {
          console.error('CSV parse errors:', results.errors);
          toast.error('Erreur lors de la lecture du fichier CSV');
          return;
        }

        // Validate required columns
        const requiredColumns = ['Name', 'City', 'Region'];
        const headers = results.meta.fields || [];
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        
        if (missingColumns.length > 0) {
          toast.error(`Colonnes manquantes: ${missingColumns.join(', ')}. Les colonnes requises sont: Name, City, Region`);
          return;
        }

        // Clean and validate data
        const cleanData = results.data
          .filter(row => row.Name && row.City && row.Region) // Remove rows with empty required fields
          .map(row => ({
            name: row.Name.trim(),
            city: row.City.trim(),
            region: row.Region.trim()
          }));

        if (cleanData.length === 0) {
          toast.error('Aucune donnée valide trouvée dans le fichier CSV');
          return;
        }

        setCsvData(cleanData);
        setCsvPreview(cleanData.slice(0, 10)); // Show first 10 rows for preview
        setImportStep(2);
      },
      error: (error) => {
        console.error('CSV parse error:', error);
        toast.error('Erreur lors de la lecture du fichier CSV');
      }
    });
  };

  const processImport = async () => {
    if (!csvData || csvData.length === 0) {
      toast.error('Aucune donnée à importer');
      return;
    }
  
    setIsImporting(true);
    setImportStep(3);
  
    const results = {
      processed: 0,
      regionsCreated: 0,
      villesCreated: 0,
      pharmaciesCreated: 0,
      duplicates: 0,
      errors: []
    };
  
    try {
      // Get existing data
      const existingRegions = regions.reduce((acc, region) => {
        acc[region.nomRegion.toLowerCase()] = region;
        return acc;
      }, {});
  
      const existingVilles = villesAll.reduce((acc, ville) => {
        const key = `${ville.nomVilleVillage.toLowerCase()}_${ville.regionId}`;
        acc[key] = ville;
        return acc;
      }, {});
  
      const existingPharmacies = pharmacies.reduce((acc, pharmacy) => {
        const key = `${pharmacy.namePharmacy.toLowerCase()}_${pharmacy.regionId}_${pharmacy.villeId}`;
        acc[key] = pharmacy;
        return acc;
      }, {});
  
      // ✅ Process regions first - only create if they don't exist
      const regionMap = { ...existingRegions };
      const uniqueRegions = [...new Set(csvData.map(row => row.region))];
      
      for (const regionName of uniqueRegions) {
        const regionKey = regionName.toLowerCase();
        if (!regionMap[regionKey]) {
          try {
            const response = await createRegion({
              variables: {
                createRegionInput: {
                  nomRegion: regionName
                }
              }
            });
            if (response.data?.createRegion) {
              regionMap[regionKey] = response.data.createRegion;
              results.regionsCreated++;
            }
          } catch (error) {
            console.error(`Error creating region ${regionName}:`, error);
            // ✅ Check if error is due to existing region
            if (error.message?.includes('Une région avec ce nom existe déjà')) {
              // Region already exists, try to find it
              const existingRegion = regions.find(r => r.nomRegion.toLowerCase() === regionKey);
              if (existingRegion) {
                regionMap[regionKey] = existingRegion;
                console.log(`Using existing region: ${regionName}`);
              } else {
                results.errors.push(`Erreur région "${regionName}": ${error.message}`);
              }
            } else {
              results.errors.push(`Erreur création région "${regionName}": ${error.message}`);
            }
          }
        }
      }
  
      // ✅ Process villes - only create if they don't exist
      const villeMap = { ...existingVilles };
      const uniqueVilles = csvData.reduce((acc, row) => {
        const region = regionMap[row.region.toLowerCase()];
        if (region) {
          const key = `${row.city}_${region.id}`;
          acc[key] = { city: row.city, regionId: region.id };
        }
        return acc;
      }, {});
  
      for (const [key, villeData] of Object.entries(uniqueVilles)) {
        const villeKey = `${villeData.city.toLowerCase()}_${villeData.regionId}`;
        if (!villeMap[villeKey]) {
          try {
            const response = await createVille({
              variables: {
                createVilleInput: {
                  nomVilleVillage: villeData.city,
                  regionId: villeData.regionId
                }
              }
            });
            if (response.data?.createVille) {
              villeMap[villeKey] = response.data.createVille;
              results.villesCreated++;
            }
          } catch (error) {
            console.error(`Error creating ville ${villeData.city}:`, error);
            // ✅ Check if error is due to existing ville
            if (error.message?.includes('Une ville avec ce nom existe déjà')) {
              // Ville already exists, try to find it
              const existingVille = villesAll.find(v => 
                v.nomVilleVillage.toLowerCase() === villeData.city.toLowerCase() && 
                v.regionId === villeData.regionId
              );
              if (existingVille) {
                villeMap[villeKey] = existingVille;
                console.log(`Using existing ville: ${villeData.city}`);
              } else {
                results.errors.push(`Erreur ville "${villeData.city}": ${error.message}`);
              }
            } else {
              results.errors.push(`Erreur création ville "${villeData.city}": ${error.message}`);
            }
          }
        }
      }
  
      // ✅ Process pharmacies - create without userId
      for (const row of csvData) {
        results.processed++;
        
        const regionKey = row.region.toLowerCase();
        const region = regionMap[regionKey];
        
        if (!region) {
          results.errors.push(`Région non trouvée pour "${row.name}": ${row.region}`);
          continue;
        }
  
        const villeKey = `${row.city.toLowerCase()}_${region.id}`;
        const ville = villeMap[villeKey];
        
        if (!ville) {
          results.errors.push(`Ville non trouvée pour "${row.name}": ${row.city}`);
          continue;
        }
  
        const pharmacyKey = `${row.name.toLowerCase()}_${region.id}_${ville.id}`;
        
        if (existingPharmacies[pharmacyKey]) {
          results.duplicates++;
          continue;
        }
  
        try {
          const response = await createPharmacy({
            variables: {
              createPharmacyInput: {
                namePharmacy: row.name,
                telPharmacy: null,
                regionId: region.id,
                villeId: ville.id,
                // ✅ No userId - let backend handle it
                piecesJointes: null
              }
            }
          });
          
          if (response.data?.createPharmacy) {
            results.pharmaciesCreated++;
            existingPharmacies[pharmacyKey] = response.data.createPharmacy;
          }
        } catch (error) {
          console.error(`Error creating pharmacy ${row.name}:`, error);
          results.errors.push(`Erreur création pharmacie "${row.name}": ${error.message}`);
        }
      }
  
      setImportResults(results);
  
      // Show success message
      if (results.errors.length === 0) {
        toast.success(`Import terminé avec succès! ${results.regionsCreated} régions, ${results.villesCreated} villes et ${results.pharmaciesCreated} pharmacies créées.`);
      } else {
        toast.warning(`Import terminé avec ${results.errors.length} erreur(s). Voir les détails ci-dessous.`);
      }
  
      // Refetch data to update the table
      setCurrentPage(1);
      await Promise.all([
        refetchPharmacies(), // This will refetch the current page
        refetchRegions(),
        refetchVillesAll()
      ]);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Erreur lors de l\'import: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  const resetImport = () => {
    setCsvFile(null);
    setCsvData([]);
    setCsvPreview([]);
    setImportResults(null);
    setImportStep(1);
    setIsImportModalOpen(false);
  };

  const handleEdit = (pharmacy) => {
    setCurrentPharmacy(pharmacy);
    setFormData({
      namePharmacy: pharmacy.namePharmacy || '',
      telPharmacy: pharmacy.telPharmacy || '',
      regionId: pharmacy.regionId || '',
      villeId: pharmacy.villeId || '',
      userId: pharmacy.userId || '',
      piecesJointes: ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleCreateNew = () => {
    setCurrentPharmacy(null);
    setFormData({
      namePharmacy: '',
      telPharmacy: '',
      regionId: '',
      villeId: '',
      userId: '',
      piecesJointes: ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleDelete = (pharmacy) => {
    setCurrentPharmacy(pharmacy);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!currentPharmacy) return;
    
    try {
      await deletePharmacy({
        variables: {
          id: currentPharmacy.id
        }
      });
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is filled
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }

    // Reset ville when region changes and refetch villes
    if (name === 'regionId') {
      setFormData(prev => ({ ...prev, villeId: '' }));
      if (value) {
        refetchVilles({ regionId: value });
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.namePharmacy.trim()) {
      errors.namePharmacy = 'Le nom de la pharmacie est requis';
    }
    
    if (!formData.regionId) {
      errors.regionId = 'La région est requise';
    }
    
    if (!formData.villeId) {
      errors.villeId = 'La ville est requise';
    }
    
    if (formData.telPharmacy && !/^(\+212\s?[567]\d{2}\s?\d{3}\s?\d{3})$/.test(formData.telPharmacy.replace(/\s/g, ''))) {
      errors.telPharmacy = 'Format de téléphone invalide (ex: +212 5XX XXX XXX)';
    }
    
    // ✅ Make userId validation optional - only validate if provided
    if (formData.userId && formData.userId.trim()) {
      // Check for uniqueness only if userId is provided
      if (!currentPharmacy) {
        const userAlreadyAssigned = pharmacies.some(pharmacy => 
          pharmacy.userId === formData.userId
        );
        if (userAlreadyAssigned) {
          errors.userId = 'Cet utilisateur est déjà propriétaire d\'une autre pharmacie';
        }
      }
      
      if (currentPharmacy && formData.userId !== currentPharmacy.userId) {
        const userAlreadyAssigned = pharmacies.some(pharmacy => 
          pharmacy.userId === formData.userId && pharmacy.id !== currentPharmacy.id
        );
        if (userAlreadyAssigned) {
          errors.userId = 'Cet utilisateur est déjà propriétaire d\'une autre pharmacie';
        }
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      // Prepare input data
      const inputData = {
        namePharmacy: formData.namePharmacy.trim(),
        telPharmacy: formData.telPharmacy ? formData.telPharmacy.trim() : null,
        regionId: formData.regionId || null,
        villeId: formData.villeId || null,
        piecesJointes: formData.piecesJointes || null
      };
  
      // ✅ Only include userId if it's provided and not empty
      if (formData.userId && formData.userId.trim()) {
        inputData.userId = formData.userId;
      }
      // If userId is empty, don't include it in the input (let backend handle it)
  
      if (currentPharmacy) {
        // Update existing pharmacy
        await updatePharmacy({
          variables: {
            updatePharmacyInput: {
              id: currentPharmacy.id,
              ...inputData
            }
          }
        });
      } else {
        // Create new pharmacy
        await createPharmacy({
          variables: {
            createPharmacyInput: inputData
          }
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const isLoading = pharmaciesLoading || regionsLoading || usersLoading;
  const isSubmitting = createLoading || updateLoading;

  // Handle loading and error states
  if (pharmaciesError && !pharmaciesData) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <div className="flex items-center">
          <AlertCircle className="mr-2" size={20} />
          <span>Erreur lors du chargement des pharmacies: {pharmaciesError.message}</span>
        </div>
        <button 
          onClick={() => refetchPharmacies()} 
          className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  // Table columns configuration
  const tableProps = {
    title: "Gestion des Pharmacies",
    subtitle: `${pagination?.totalItems || 0} pharmacie(s) au total`,
    data: pharmacies,
    columns: [
      {
        Header: 'Nom',
        accessor: 'namePharmacy',
        sortable: true,
        searchable: true,
        Cell: ({ value }) => (
          <div className="flex items-center">
            <Pill size={16} className="mr-2 text-green-500" />
            <span className="font-medium">{value}</span>
          </div>
        )
      },
      {
        Header: 'Région',
        accessor: 'region',
        sortable: true,
        searchable: true,
        Cell: ({ row }) => (
          <span className="text-gray-600">
            {row.region?.nomRegion || 'N/A'}
          </span>
        )
      },
      {
        Header: 'Ville',
        accessor: 'ville',
        sortable: true,
        searchable: true,
        Cell: ({ row }) => (
          <span className="text-gray-600">
            {row.ville?.nomVilleVillage || 'N/A'}
          </span>
        )
      },
      {
        Header: 'Téléphone',
        accessor: 'telPharmacy',
        sortable: false,
        searchable: true,
        Cell: ({ value }) => value || '-'
      },
      {
        Header: 'Propriétaire',
        accessor: 'owner',
        sortable: false,
        searchable: true,
        Cell: ({ row }) => {
          const owner = row.owner;
          if (!owner) return 'N/A';
          return (
            <div>
              <div className="font-medium text-sm">
                {owner.firstName && owner.lastName 
                  ? `${owner.firstName} ${owner.lastName}` 
                  : owner.email}
              </div>
              <div className="text-sm text-gray-500">{owner.email}</div>
            </div>
          );
        }
      },
      {
        Header: 'Commission (DH)',
        accessor: 'commissionBalance',
        sortable: true,
        searchable: false,
        Cell: ({ value }) => {
          const amount = value || 0;
          return (
            <span className={`font-medium ${amount > 0 ? 'text-green-600' : 'text-gray-500'}`}>
              {amount.toFixed(2)} DH
            </span>
          );
        }
      }
    ],
    onEdit: handleEdit,
    onDelete: handleDelete,
    onCreate: () => setIsModalOpen(true),
    loading: pharmaciesLoading,
    emptyMessage: "Aucune pharmacie trouvée",
    // ✅ NEW: Pass pagination props
    pagination: {
      currentPage: pagination?.currentPage || 1,
      totalPages: pagination?.totalPages || 1,
      totalItems: pagination?.totalItems || 0,
      itemsPerPage: pagination?.itemsPerPage || itemsPerPage,
      hasNextPage: pagination?.hasNextPage || false,
      hasPreviousPage: pagination?.hasPreviousPage || false,
      onPageChange: handlePageChange,
      onItemsPerPageChange: handleItemsPerPageChange
    },
    // ✅ NEW: Pass search and sort props
    search: {
      value: searchTerm,
      onChange: handleSearch,
      placeholder: "Rechercher par nom, région, ville, téléphone..."
    },
    sort: {
      column: sortBy,
      direction: sortOrder,
      onSort: handleSort
    }
  };

  return (
    <div>
      {/* Custom Header with Import Button */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Pharmacies</h1>
            <p className="mt-1 text-sm text-gray-500">Gérez les pharmacies partenaires de la plateforme</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importer CSV
            </button>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Pill className="w-4 h-4 mr-2" />
              Créer
            </button>
          </div>
        </div>
      </div>

      <DataTableSettings {...tableProps} />
      
      {/* Create/Edit Modal */}
      <ModalComponent
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentPharmacy ? 'Modifier la pharmacie' : 'Créer une nouvelle pharmacie'}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de la pharmacie *
            </label>
            <input
              type="text"
              name="namePharmacy"
              value={formData.namePharmacy}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.namePharmacy ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Entrez le nom de la pharmacie"
            />
            {formErrors.namePharmacy && (
              <p className="mt-1 text-sm text-red-600">{formErrors.namePharmacy}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone
            </label>
            <input
              type="text"
              name="telPharmacy"
              value={formData.telPharmacy}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.telPharmacy ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ex: +212 5XX XXX XXX"
            />
            {formErrors.telPharmacy && (
              <p className="mt-1 text-sm text-red-600">{formErrors.telPharmacy}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Région
              </label>
              <select
                name="regionId"
                value={formData.regionId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Sélectionnez une région</option>
                {regions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.nomRegion}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ville/Village
              </label>
              <select
                name="villeId"
                value={formData.villeId}
                onChange={handleInputChange}
                disabled={!formData.regionId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
              >
                <option value="">Sélectionnez une ville</option>
                {filteredVilles.map((ville) => (
                  <option key={ville.id} value={ville.id}>
                    {ville.nomVilleVillage}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Propriétaire (Pharmacien) *
            </label>
            <select
              name="userId"
              value={formData.userId}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.userId ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionnez un pharmacien</option>
              {users.filter(user => user.isActive).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName} (${user.email})`
                    : user.email}
                </option>
              ))}
            </select>
            {formErrors.userId && (
              <p className="mt-1 text-sm text-red-600">{formErrors.userId}</p>
            )}
          </div>
        </div>
      </ModalComponent>

      {/* CSV Import Modal */}
      <ModalComponent
        isOpen={isImportModalOpen}
        onClose={resetImport}
        title="Importer des Pharmacies depuis un CSV"
        size="lg"
        showFooter={false}
      >
        <div className="space-y-6">
          {/* Step 1: File Selection */}
          {importStep === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Format CSV requis:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Colonne "Name": Nom de la pharmacie</li>
                  <li>• Colonne "City": Nom de la ville/village</li>
                  <li>• Colonne "Region": Nom de la région</li>
                  <li>• Les régions et villes manquantes seront créées automatiquement</li>
                  <li>• Les doublons seront ignorés</li>
                  <li>• Les pharmacies seront créées sans propriétaire assigné</li>
                </ul>
              </div>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <div className="space-y-2">
                  <label className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      Sélectionnez un fichier CSV
                    </span>
                    <input
                      type="file"
                      className="sr-only"
                      accept=".csv"
                      onChange={handleFileSelect}
                    />
                  </label>
                  <p className="text-xs text-gray-500">Taille maximale: 5MB</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {importStep === 2 && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-2">Fichier traité avec succès!</h4>
                <p className="text-sm text-green-700">
                  {csvData.length} enregistrement(s) trouvé(s). Aperçu des 10 premiers:
                </p>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pharmacie
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ville/Village
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Région
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {csvPreview.map((row, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {row.city}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {row.region}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {csvData.length > 10 && (
                <p className="text-sm text-gray-500 text-center">
                  ... et {csvData.length - 10} autre(s) enregistrement(s)
                </p>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">Information importante:</h4>
                <p className="text-sm text-yellow-700">
                  Les pharmacies seront créées sans propriétaire assigné. 
                  Vous pourrez assigner les propriétaires individuellement après l'import.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {importStep === 3 && (
            <div className="space-y-4">
              {isImporting ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mr-3" />
                  <span className="text-gray-600">Import en cours...</span>
                </div>
              ) : importResults && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{importResults.processed}</div>
                      <div className="text-sm text-blue-700">Traités</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{importResults.regionsCreated}</div>
                      <div className="text-sm text-green-700">Régions créées</div>
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-600">{importResults.villesCreated}</div>
                      <div className="text-sm text-emerald-700">Villes créées</div>
                    </div>
                    <div className="bg-teal-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-teal-600">{importResults.pharmaciesCreated}</div>
                      <div className="text-sm text-teal-700">Pharmacies créées</div>
                    </div>
                  </div>

                  {importResults.duplicates > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-medium text-yellow-800 mb-2 flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        Doublons ignorés ({importResults.duplicates})
                      </h4>
                      <p className="text-sm text-yellow-700">
                        {importResults.duplicates} pharmacie(s) avec le même nom dans la même ville ont été ignorées car elles existent déjà.
                      </p>
                    </div>
                  )}

                  {importResults.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-medium text-red-800 mb-2 flex items-center">
                        <XCircle className="w-5 h-5 mr-2" />
                        Erreurs rencontrées ({importResults.errors.length})
                      </h4>
                      <div className="max-h-32 overflow-y-auto">
                        {importResults.errors.map((error, index) => (
                          <p key={index} className="text-sm text-red-700 mb-1">
                            • {error}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {importResults.errors.length === 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-medium text-green-800 flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Import terminé avec succès!
                      </h4>
                      <p className="text-sm text-green-700 mt-1">
                        Toutes les données ont été importées sans erreur. Les pharmacies sont prêtes à être assignées à des propriétaires.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Custom Footer */}
        <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={resetImport}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={isImporting}
          >
            Annuler
          </button>
          <div className="flex space-x-3">
            {importStep === 2 && (
              <button
                onClick={() => setImportStep(1)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                disabled={isImporting}
              >
                Retour
              </button>
            )}
            {importStep === 2 && (
              <button
                onClick={processImport}
                disabled={isImporting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  'Démarrer l\'import'
                )}
              </button>
            )}
          </div>
        </div>
      </ModalComponent>
      
      {/* Confirm Delete Modal */}
      <ModalComponent
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setCurrentPharmacy(null);
        }}
        title="Confirmer la suppression"
        onSubmit={confirmDelete}
        isSubmitting={deleteLoading}
        submitLabel="Supprimer"
      >
        <p className="text-gray-500 mt-2">
          Cette action est irréversible. Êtes-vous sûr de vouloir supprimer cette pharmacie ?
        </p>
        {currentPharmacy && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="font-medium text-gray-900">{currentPharmacy.namePharmacy}</p>
            <p className="text-sm text-gray-600">
              {currentPharmacy.region?.nomRegion} - {currentPharmacy.ville?.nomVilleVillage}
            </p>
          </div>
        )}
      </ModalComponent>
    </div>
  );
};

export default PharmacieSettings;